import React, { Component } from 'react';
import { ActionMeta, GroupBase, InputActionMeta, MultiValue, PropsValue, SelectInstance, SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { FormFieldFunctions } from '../../interfaces/FormField/FormField';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import Option from '../../interfaces/Option/Option';
import Snapshot from '../../interfaces/Snapshot/Snapshot';
import { CustomDropdownIndicator, CustomInput } from '../FormFieldSelect/FormFieldSelectPartials';
import { formFieldSelectStylesForCustomerDataPage } from '../FormFieldSelect/FormFieldSelectStyles';
import FormFieldSelectStreetProps from './FormFieldSelectStreet.props';
import FormFieldSelectStreetState from './FormFieldSelectStreet.state';

export default class FormFieldSelectStreet extends Component<FormFieldSelectStreetProps, FormFieldSelectStreetState> implements FormFieldFunctions {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private selectElement: React.RefObject<HTMLDivElement>;
    private select: React.RefObject<SelectInstance<Option, false, GroupBase<Option>>>;

    constructor(props: FormFieldSelectStreetProps) {
        super(props);
        this.state = {
            selected: this.props.selected,
            city: this.props.city,
            plz: this.props.plz
        };

        this.selectElement = React.createRef<HTMLDivElement>();
        this.select = React.createRef<SelectInstance<Option, false, GroupBase<Option>>>();
        this.handleSelectChange = this.handleSelectChange.bind(this);
        this.getStreetList = this.getStreetList.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.isValid = this.isValid.bind(this);
        this.toggleValidationError = this.toggleValidationError.bind(this);
    }

    handleSelectChange(newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) {
        this.setState({
            selected: (newValue as Option).value,
        }, this.onBlur);
    }

    resetField() {
        this.setState({
            options: [],
            selected: undefined,
        });
        this.props.formFieldNumber?.setState({
            readOnly: true,
            selected: '',
        });
    }

    updateAndNotify = () => {
        this.setState({
            selected: this.props.selected,
            city: this.props.city,
            plz: this.props.plz
        }, () => {
            this.props.formFieldNumber?.setState({
                readOnly: this.props.isLoggedIn && this.props.formFieldNumber.isValid() ? true : false,
            });
        });
    }

    getSnapshotBeforeUpdate(prevProps: FormFieldSelectStreetProps): Snapshot {
        return { notifyRequired: prevProps.selected !== this.props.selected };
    }

    componentDidUpdate(prevProps: FormFieldSelectStreetProps, prevState: FormFieldSelectStreetState, snapshot: Snapshot) {
        if (snapshot.notifyRequired) {
            this.updateAndNotify();
        }
    }

    onBlur() {
        if (this.isValid()) {
            this.toggleValidationError(false);
            this.props.formFieldNumber?.setState({
                readOnly: false,
            }, this.props.formFieldNumber.getFocus);
        }
    }

    isValid() {
        if (this.props.required && (this.state.selected === undefined || this.state.selected.trim() === '')) {
            return false;
        }

        return true;
    }

    toggleValidationError(show: boolean) {
        if (show) {
            this.selectElement.current?.parentElement?.classList.add('form__field--error');
        } else {
            this.selectElement.current?.parentElement?.classList.remove('form__field--error');
        }
    }

    getFocus() {
        this.select.current?.focus();
    }

    async getStreetList(newValue: string): Promise<Option[]> {
        if (newValue.trim() === '') {
            return [] as Option[];
        }

        const response = await this.context.requestProvider.postData(
            '/jportal/wsrest/recherche3/addressSuggestStreet',
            {
                portalId: 'jurisw',
                plz: this.state.plz,
                city: this.state.city,
                streetPrefix: newValue, // has to be empty!
                r3ID: (new Date()).toISOString(),
            }
        );

        if (response && response.status === 200) {
            const newStreetList = (await response.json()).list || [];
            const upgradedStreetList = newStreetList.map((street: string) => {
                return {
                    label: street,
                    value: street
                } as Option
            }) as Option[];
            // console.log(newStreetList);
            // console.log(upgradedStreetList);
            this.setState({
                selected: newValue,
                options: upgradedStreetList,
            });
            return upgradedStreetList;
        }

        return [] as Option[];
    }

    getValue() {
        return this.state.selected;
    }

    render(): React.ReactNode {
        return (
            <div data-testid="FormFieldSelectStreet"
                className={
                    "select"
                    + (!!this.state.selected && this.state.selected !== 'default' ? " select--option-selected" : "")
                }
                ref={this.selectElement}>
                <div className="select__inner">
                    <div className="select__content">
                        {
                            this.props.label &&
                            <label className="select__label" htmlFor={this.props.id}>{this.props.label}</label>
                        }
                        <AsyncSelect
                            inputId={this.props.id}
                            tabIndex={undefined}
                            tabSelectsValue={false}
                            maxLength={this.props.maxLength}
                            name={this.props.name || this.props.id}
                            loadOptions={this.getStreetList}
                            value={(this.state.selected && {
                                label: this.state.selected,
                                value: this.state.selected
                            } as PropsValue<Option>) || undefined}
                            defaultOptions={this.state.options || ([] as Option[])}
                            onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
                                // console.log(actionMeta)
                                if (actionMeta.action === 'input-change') {
                                    this.setState({
                                        selected: newValue
                                    });
                                }
                            }}
                            inputValue={this.state.selected}
                            cacheOptions
                            // onInputChange={this.getPLZList}
                            placeholder={this.props.label}
                            // defaultMenuIsOpen={true} // for debug purposes
                            noOptionsMessage={() => "Keine Optionen"}
                            closeMenuOnSelect={true}
                            isDisabled={
                                (this.props.isLoggedIn && this.state.selected !== undefined)
                                || (!this.props.isLoggedIn && this.state.city === undefined && this.state.plz === undefined)
                            }
                            isMulti={false}
                            onChange={this.handleSelectChange}
                            onBlur={this.onBlur}
                            styles={formFieldSelectStylesForCustomerDataPage}
                            ref={this.select}
                            components={{
                                DropdownIndicator: CustomDropdownIndicator,
                                Input: CustomInput,
                            }} />
                    </div>
                </div>
            </div>
        );
    }
}
